import { useState } from "react";
import { Workflow, Plus, Zap, RefreshCw, FileText, Gem, ArrowRight } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import StatCard from "../../components/ui/StatCard";
import Badge from "../../components/ui/Badge";
import { useAlert } from "../../../hooks/useAlert";

import AutomationCard from "./automations/automation-card";
import AutomationFlowDrawer from "./automations/automation-flow-drawer";
import { AUTOMATIONS, AUTOMATION_KPIS, AUTOMATION_STARTERS } from "./automations/automation.data";

const KPI_ICONS = {
  flows:    { icon: Workflow, iconBg: "var(--color-primary-tint)",  iconColor: "var(--color-primary)" },
  enrolled: { icon: Zap,      iconBg: "var(--color-info-bg)",        iconColor: "var(--color-info)" },
  conv:     { icon: RefreshCw,iconBg: "var(--color-success-bg)",     iconColor: "var(--color-success)" },
  revenue:  { icon: Gem,      iconBg: "var(--color-secondary-tint)", iconColor: "var(--color-secondary)" },
};

const STARTER_ICON = { primary: FileText, high: RefreshCw, info: Workflow, success: Gem };
const STARTER_COLOR = {
  primary: { bg: "var(--color-primary-tint)",  fg: "var(--color-primary)" },
  high:    { bg: "var(--color-high-bg)",        fg: "var(--color-high)" },
  info:    { bg: "var(--color-info-bg)",        fg: "var(--color-info)" },
  success: { bg: "var(--color-success-bg)",     fg: "var(--color-success)" },
};

export default function Automations() {
  const { showAlert } = useAlert();
  const [flows, setFlows] = useState(AUTOMATIONS);
  const [selected, setSelected] = useState(null);

  const toggle = (a) => {
    const next = a.status === "active" ? "paused" : "active";
    setFlows((prev) => prev.map((f) => (f.id === a.id ? { ...f, status: next } : f)));
    if (selected?.id === a.id) setSelected((s) => ({ ...s, status: next }));
    showAlert({ type: next === "active" ? "success" : "info", message: `"${a.name}" ${next === "active" ? "activated" : "paused"}.` });
  };

  const startFrom = (s) => showAlert({ type: "info", message: `Starting new automation from "${s.name}" recipe.` });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Automations"
        subtitle="Set up journeys that nurture and re-engage contacts on autopilot."
        icon={Workflow}
        actions={
          <button
            onClick={() => showAlert({ type: "info", message: "Blank automation builder coming soon." })}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)", boxShadow: "0 4px 16px rgba(53,53,93,0.22)" }}
          >
            <Plus className="w-4 h-4" /> New Automation
          </button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {AUTOMATION_KPIS.map((k) => (
          <StatCard key={k.key} {...k} {...KPI_ICONS[k.key]} />
        ))}
      </div>

      {/* Starter recipes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-text_primary">Start from a recipe</h3>
            <p className="text-xs text-text_muted mt-0.5">Proven journeys you can launch in minutes.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {AUTOMATION_STARTERS.map((s) => {
            const Icon = STARTER_ICON[s.tone] ?? Workflow;
            const col = STARTER_COLOR[s.tone] ?? STARTER_COLOR.primary;
            return (
              <button
                key={s.id}
                onClick={() => startFrom(s)}
                className="group text-left bg-surface border border-border rounded-xl p-4 transition-all duration-200 hover:-translate-y-0.5"
                style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: col.bg }}>
                    <Icon className="w-4 h-4" style={{ color: col.fg }} />
                  </div>
                  <span className="text-xl">{s.icon}</span>
                </div>
                <h4 className="text-sm font-semibold text-text_primary leading-tight">{s.name}</h4>
                <p className="text-xs text-text_muted mt-1 leading-snug">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold mt-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Use recipe <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active flows */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold text-text_primary">Your automations</h3>
          <Badge tone="neutral">{flows.length}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {flows.map((a) => (
            <AutomationCard key={a.id} automation={a} onOpen={setSelected} onToggle={toggle} />
          ))}
        </div>
      </div>

      <AutomationFlowDrawer
        automation={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onToggle={toggle}
      />
    </div>
  );
}
